import { Trans, useLingui } from '@lingui/react/macro';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { TeamPlayer } from 'modules/teams/domain/interfaces/team-player';
import { deleteTournamentPlayerApplication } from 'modules/teams/store/players/actions';
import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { USER_DEFAULT_AVATAR } from 'shared/constants/links';
import { getBirthDay } from 'shared/utils';
import { AppDispatch } from 'store/store';
import { UDEntityRow } from 'ud-ui/components';
import UDButton from 'ud-ui/components/button';
import Modal from 'ud-ui/components/modal';

interface TeamPlayerRowProps {
  teamId: number;
  player: TeamPlayer;
  /** Порядковый номер строки, уже готовый к показу. */
  index: number;
}

/**
 * Строка состава команды: номер, аватар, ФИО, дата рождения, «Редактировать» и
 * «Исключить» — плюс окно подтверждения исключения.
 *
 * Своё окно и свой флаг загрузки у каждой строки: общий на весь слайс крутил
 * спиннер сразу на всех игроках.
 */
const TeamPlayerRow = ({ teamId, player, index }: TeamPlayerRowProps) => {
  const { t } = useLingui();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const open = useCallback(() => setIsModalOpen(true), []);
  const close = useCallback(() => setIsModalOpen(false), []);
  const [isLoading, setIsLoading] = useState(false);

  const name = [player.lastName, player.firstName, player.middleName].filter(Boolean).join(' ');
  // Формат даты рождения — общий для всех списков карточки: `getBirthDay`,
  // возраст в скобках. Своя сборка через `date-fns` давала «01.02.1990, 36 лет»
  // и расходилась с «Участниками» и «Листом ожидания».
  const fullBirthDate = getBirthDay({ birthDate: player.birthDate ? new Date(player.birthDate) : undefined });

  const handleKick = useCallback(async () => {
    setIsLoading(true);

    try {
      await dispatch(deleteTournamentPlayerApplication({ userId: player.userId, teamId })).unwrap();

      toast.success(t`Игрок успешно исключен`);
      close();
    } catch (e) {
      toast.error(t`Не удалось исключить игрока`);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, player.userId, teamId, close, t]);

  const handleClickEdit = useCallback(() => {
    navigate(Pages.PLAYERS.builders.view(player.userId), { state: { fromTeam: teamId } });
  }, [navigate, player.userId, teamId]);

  return (
    <>
      <UDEntityRow
        index={index}
        avatar={player.avatar || USER_DEFAULT_AVATAR}
        title={name}
        subtitle={fullBirthDate}
        actions={
          <>
            <UDButton variant="primary" onClick={handleClickEdit}>
              <Trans>Редактировать</Trans>
            </UDButton>

            <UDButton variant="secondary" onClick={open}>
              <Trans>Исключить</Trans>
            </UDButton>
          </>
        }
      />

      <Modal.Confirm
        show={isModalOpen}
        title={t`Исключить игрока «${name}»?`}
        description={t`Это действие необратимо.`}
        confirmText={t`Исключить`}
        isLoading={isLoading}
        onConfirm={handleKick}
        onCancel={close}
      />
    </>
  );
};

export default TeamPlayerRow;
